import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarDays,
  ChevronRight,
  Flame,
  Star,
  BookOpen,
  Trophy,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { useContentAccess } from '../hooks/useContentAccess';
import { getDailyScenarioForAgeGroups } from '../data/scenarios';
import { getDailyAffirmationForTier } from '../data/affirmations';
import { CATEGORIES } from '../data/categories';
import { BUNDLES } from '../data/bundles';
import { getCurrentWeeklyChallenge } from '../data/weeklyChallenges';
import EllieAvatar from '../components/EllieAvatar';
import GuestGate from '../components/GuestGate';

function getTimeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, type: 'spring', stiffness: 220, damping: 22 },
  }),
};

export default function Home() {
  const navigate = useNavigate();
  const user = useAppStore((s) => s.user);
  const appMode = useAppStore((s) => s.appMode);
  const getActiveChild = useAppStore((s) => s.getActiveChild);
  const { isGuest, isPremium } = useContentAccess();
  const [showGuestGate, setShowGuestGate] = useState(false);

  const activeChild = getActiveChild();
  const isKidMode = appMode.mode === 'kid';

  // Collect all children's age groups and surface a relevant daily scenario
  const childAgeGroups = (user?.childProfiles ?? []).map((c) => c.ageGroup);
  const featuredScenario = getDailyScenarioForAgeGroups(childAgeGroups);

  const weeklyChallenge = getCurrentWeeklyChallenge(user?.createdAt);
  const dailyAffirmation = getDailyAffirmationForTier(
    user?.tier ?? 'guest',
    childAgeGroups as Array<'4-6' | '7-9' | '10-12'>
  );

  const parentName = user?.name ?? 'Parent';
  const greeting = `Good ${getTimeOfDay()}, ${parentName}!`;

  const recommendedBundles = BUNDLES.slice(0, 3);

  return (
    <>
    <div className="px-4 pt-4 pb-24 space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        custom={0}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="pt-10"
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18, delay: 0.1 }}
          >
            <EllieAvatar size={56} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-gray-800 leading-tight">
              {greeting}
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">
              Let's learn something new today
            </p>
          </div>
          <span
            className={`shrink-0 mt-1 px-2.5 py-1 rounded-full text-xs font-bold ${
              isKidMode
                ? 'bg-warm-100 text-warm-700'
                : 'bg-primary-50 text-primary-700'
            }`}
          >
            {isKidMode ? '⭐ Kid Mode' : '🛡️ Parent'}
          </span>
        </div>
      </motion.div>

      {/* Today's Scenario */}
      <motion.section
        custom={1}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays size={16} className="text-primary-500" />
          <span className="text-xs font-bold text-primary-600 uppercase tracking-wide">
            Today's Scenario
          </span>
        </div>

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() =>
            navigate(`/library/scenario/${featuredScenario.id}`)
          }
          className="w-full text-left bg-gradient-to-br from-primary-600 to-violet-700 rounded-3xl p-5 shadow-lg shadow-primary-200/50 hover:shadow-xl hover:shadow-primary-300/40 transition-all duration-200"
        >
          <div className="flex items-start justify-between mb-3">
            <span className="text-2xl">
              {
                CATEGORIES.find(
                  (c) => c.id === featuredScenario.categoryId
                )?.icon ?? '📚'
              }
            </span>
            <span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              Ages {featuredScenario.ageGroup}
            </span>
          </div>
          <h3 className="font-display text-xl font-bold text-white mb-1 leading-tight">
            {featuredScenario.title}
          </h3>
          <p className="text-white/75 text-sm line-clamp-2">
            {featuredScenario.scenarioDescription}
          </p>
          <div className="flex items-center gap-1 mt-3 text-white/90 text-xs font-semibold">
            Tap to practise <ChevronRight size={14} />
          </div>
        </motion.button>
      </motion.section>

      {/* Today's Affirmation */}
      <motion.section
        custom={1.5}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-base">✨</span>
            <span className="text-xs font-bold text-primary-600 uppercase tracking-wide">
              Today's Affirmation
            </span>
          </div>
          <button
            onClick={() => navigate('/affirmations')}
            className="text-xs text-primary-600 font-semibold flex items-center gap-0.5"
          >
            See all <ChevronRight size={13} />
          </button>
        </div>

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/affirmations')}
          className="w-full text-left bg-gradient-to-br from-violet-50 to-primary-50 border border-primary-100 rounded-3xl p-5 hover:shadow-md transition-all duration-200"
        >
          <p className="font-display font-extrabold text-primary-800 text-lg leading-snug mb-2">
            "{dailyAffirmation.text}"
          </p>
          <div className="flex items-center justify-between">
            <span className="bg-primary-100 text-primary-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
              {dailyAffirmation.category}
            </span>
            {!isPremium && (
              <span className="text-[10px] font-bold text-warm-600 flex items-center gap-0.5">
                ✨ Unlock all affirmations
              </span>
            )}
          </div>
        </motion.button>
      </motion.section>

      {/* Category Shortcuts */}
      <motion.section
        custom={2}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Categories
          </span>
          <button
            onClick={() => navigate('/library')}
            className="text-xs text-primary-600 font-semibold flex items-center gap-0.5"
          >
            See all <ChevronRight size={13} />
          </button>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
          {CATEGORIES.map((cat) => (
            <motion.button
              key={cat.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/library/category/${cat.id}`)}
              className="flex-shrink-0 flex flex-col items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-card hover:shadow-card-hover transition-all duration-200 min-w-[76px]"
            >
              <span className="text-2xl">{cat.icon}</span>
              <span className="text-[10px] font-semibold text-gray-600 text-center leading-tight max-w-[64px]">
                {cat.name.split(' ').slice(0, 2).join(' ')}
              </span>
            </motion.button>
          ))}
        </div>
      </motion.section>

      {/* Recommended Bundles */}
      <motion.section
        custom={3}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen size={15} className="text-primary-500" />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Recommended Bundles
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {recommendedBundles.map((bundle) => (
            <motion.button
              key={bundle.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/library/bundle/${bundle.id}`)}
              className="w-full text-left bg-white rounded-2xl p-4 shadow-card flex items-center gap-4 hover:shadow-card-hover transition-all duration-200"
            >
              <div
                className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${bundle.color} flex items-center justify-center text-2xl flex-shrink-0`}
              >
                {bundle.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-display font-bold text-gray-800 text-sm leading-tight">
                  {bundle.name}
                </h4>
                <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">
                  {bundle.description}
                </p>
                <span className="text-primary-500 text-xs font-semibold mt-1 inline-block">
                  {bundle.scenarioIds.length} scenarios
                </span>
              </div>
              <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
            </motion.button>
          ))}
        </div>
      </motion.section>

      {/* Guest teaser CTA */}
      {isGuest && (
        <motion.div
          custom={3.5}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <button
            onClick={() => setShowGuestGate(true)}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl p-5 text-left shadow-lg"
          >
            <p className="text-white font-display font-extrabold text-lg leading-tight mb-1">
              🔓 50+ more scenarios await
            </p>
            <p className="text-white/80 text-sm">
              Create your free account to unlock all age-matched content →
            </p>
          </button>
        </motion.div>
      )}

      {/* Family Challenge */}
      <motion.section
        custom={4}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={15} className="text-warm-500" />
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Family Challenge
          </span>
        </div>

        <div className="bg-gradient-to-br from-warm-50 to-amber-50 border border-warm-200 rounded-3xl p-5">
          <div className="flex items-start gap-3">
            <span className="text-4xl">{weeklyChallenge.icon}</span>
            <div className="flex-1">
              <p className="text-warm-600 text-[10px] font-bold uppercase tracking-wide mb-0.5">
                Week {weeklyChallenge.weekNumber} · {weeklyChallenge.theme}
              </p>
              <h3 className="font-display font-bold text-gray-800 text-base">
                {weeklyChallenge.title}
              </h3>
              <p className="text-gray-500 text-xs mt-1 line-clamp-2">
                {weeklyChallenge.challengeDescription}
              </p>
            </div>
          </div>

          {/* Talking points preview */}
          <div className="mt-3 space-y-1">
            {weeklyChallenge.talkingPoints.slice(0, 2).map((point, i) => (
              <p key={i} className="text-gray-500 text-xs flex items-start gap-1.5">
                <span className="text-warm-400 font-bold shrink-0">💬</span>
                <span className="line-clamp-1">{point}</span>
              </p>
            ))}
          </div>

          {/* Badge */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-lg">{weeklyChallenge.badgeEmoji}</span>
            <span className="text-xs font-semibold text-warm-700">
              Earn: {weeklyChallenge.badgeName}
            </span>
          </div>
        </div>
      </motion.section>

      {/* Child Progress — hidden for guests */}
      {activeChild && (
        <motion.section
          custom={5}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center gap-2 mb-3">
            <Star size={15} className="text-primary-500" />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              {activeChild.name}'s Progress
            </span>
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-card">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-5xl">{activeChild.avatar}</span>
              <div>
                <h3 className="font-display font-bold text-gray-800 text-lg">
                  {activeChild.name}
                </h3>
                <span className="bg-primary-50 text-primary-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  Ages {activeChild.ageGroup}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-primary-50 rounded-2xl p-3 text-center">
                <div className="font-display font-extrabold text-xl text-primary-700">
                  {activeChild.points}
                </div>
                <div className="text-primary-500 text-xs font-semibold mt-0.5">
                  Points
                </div>
              </div>
              <div className="bg-warm-50 rounded-2xl p-3 text-center">
                <div className="font-display font-extrabold text-xl text-warm-600 flex items-center justify-center gap-1">
                  {activeChild.streak}
                  <Flame size={16} className="text-warm-500" />
                </div>
                <div className="text-warm-600 text-xs font-semibold mt-0.5">
                  Streak
                </div>
              </div>
              <div className="bg-green-50 rounded-2xl p-3 text-center">
                <div className="font-display font-extrabold text-xl text-green-700">
                  {activeChild.practicedScenarioIds.length}
                </div>
                <div className="text-green-600 text-xs font-semibold mt-0.5">
                  Practised
                </div>
              </div>
            </div>
          </div>
        </motion.section>
      )}
    </div>
    <GuestGate visible={showGuestGate} onDismiss={() => setShowGuestGate(false)} />
  </>
  );
}
